import { CourseApi, CourseScheduleDetailsApi } from "api";
import { useCourseSessionsOptions } from "api/hooks";
import { PaziaButtonSwitch } from "components/atoms";
import { EmptyState } from "components/molecules";
import { FormProvider, useForm } from "react-hook-form";
import { PaziaLoader } from "components/atoms";
import { BaseScheduleFormSchema } from "./BaseScheduleForm";
import { ScheduleBreakForm } from "./ScheduleBreakForm";
import { ScheduleTopicForm, ScheduleTopicFormSchema } from "./ScheduleTopicForm";
import { ScheduleTopicGroupForm, ScheduleTopicGroupFormSchema } from "./ScheduleTopicGroupForm";

interface ScheduleTypeSelectionFormProps {
  onBreakSubmit: (data: BaseScheduleFormSchema) => Promise<void>;
  onTopicSubmit: (data: ScheduleTopicFormSchema) => Promise<boolean | void>;
  onTopicGroupSubmit: (data: ScheduleTopicGroupFormSchema) => Promise<boolean | void>;
  course: CourseApi;
  schedule?: CourseScheduleDetailsApi;
  formStyle: React.CSSProperties;
  formClassName?: string;
}

export const ScheduleTypeSelectionForm = ({
  onTopicSubmit,
  onTopicGroupSubmit,
  onBreakSubmit,
  course,
  schedule,
  formStyle,
  formClassName = "",
}: ScheduleTypeSelectionFormProps): JSX.Element => {
  const {
    data: { id: courseId },
  } = course;
  const { loading: loadingSessions, options: sessionsOptions } = useCourseSessionsOptions(courseId);
  const methods = useForm({
    defaultValues: {
      eventType: schedule?.schedule?.topicId
        ? EVENT_TYPE.TOPIC
        : schedule?.schedule?.topicGroupId
          ? EVENT_TYPE.GROUP
          : EVENT_TYPE.GROUP,
    },
  });
  const { watch } = methods;
  const eventType = watch("eventType") as EventType;
  return (
    <>
      <FormProvider {...methods}>
        <form className="mb-4">
          <PaziaButtonSwitch
            name="eventType"
            disabled={!!schedule}
            a={EVENT_TYPE.GROUP}
            b={EVENT_TYPE.TOPIC}
            c={EVENT_TYPE.BREAK}
          />
        </form>
      </FormProvider>
      {loadingSessions ? (
        <div className="flex justify-center">
          <div>
            <PaziaLoader />
          </div>
        </div>
      ) : sessionsOptions?.length < 1 ? (
        <div className="p-4 text-center">
          <EmptyState
            title="Brak zjazdów"
            description="Nie skonfigurowano jeszcze żadnych zjazdów dla tego kursu. Przejdź do zakładki Zjazdy i dodaj nowy."
          />
        </div>
      ) : (
        <>
          {eventType === EVENT_TYPE.TOPIC && (
            <ScheduleTopicForm
              formStyle={formStyle}
              formClassName={formClassName}
              course={course}
              onSubmit={onTopicSubmit}
              sessionsOptions={sessionsOptions}
              item={schedule}
            />
          )}
          {eventType === EVENT_TYPE.GROUP && (
            <ScheduleTopicGroupForm
              formStyle={formStyle}
              formClassName={formClassName}
              course={course}
              onSubmit={onTopicGroupSubmit}
              sessionsOptions={sessionsOptions}
              item={schedule}
            />
          )}
          {eventType === EVENT_TYPE.BREAK && (
            <ScheduleBreakForm
              formStyle={formStyle}
              formClassName={formClassName}
              onSubmit={onBreakSubmit}
              sessionsOptions={sessionsOptions}
              item={schedule}
            />
          )}
        </>
      )}
    </>
  );
};

const EVENT_TYPE = {
  TOPIC: "Temat",
  GROUP: "Grupa",
  BREAK: "Przerwa",
} as const;

type EventType = (typeof EVENT_TYPE)[keyof typeof EVENT_TYPE];
